'use client';

import { useTranslations } from 'next-intl';
import {
  DEFAULT_DRIP_DAYS,
  OfferReviewDialog,
  OfferReviewProps,
  PurchaseButton,
} from '@wwe-portal/ui/features/offer';
import {
  createPortableDialog,
  registerDialog,
  removeDialog,
  showDialog,
  showDialogById,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog';
import { usePipStore } from '@wwe-portal/web/providers/stores/use-pip-store';
import { OfferData, OfferItem } from '@wwe-portal/entities/offer.type';
import { PointRule } from '@wwe-portal/entities/rbe.type';
import {
  useDismissOtoOffer,
  useGetCurrencies,
} from '@wwe-portal/web/providers/queries/offer';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { OtoCloseConfirmDialog } from '@wwe-portal/web/components/offer-card/oto-close-confirm-dialog';
import { useStoreData } from './use-store-data';
import {
  cardStatusLabel,
  getCardStatus,
  getRewardItems,
  parseRegionalOfferPrice,
} from '@wwe-portal/web/utils/offer/mapper';
import { useGetUserInfo } from '@wwe-portal/web/providers/queries/user-info';
import { DIALOG_ID_SPECIAL_OFFER_PREVIEW } from '@wwe-portal/web/utils/constants/keys';
import { usePendingPayment } from '../payment/use-xsolla-payment';
import {
  formatQuantityByDouble,
  getEndDate,
  isDripOffer,
  isOneTimeOffer,
} from '@wwe-portal/web/utils/offer';
import {
  sendOfferButtonIsTappedEvent,
  specialOfferDialogClosed,
  specialOfferDialogShowed,
} from '@wwe-portal/web/libs/analytics/offer';
import { SysUIParameter } from '@wwe-portal/web/libs/analytics/common';
import { useIsClaimingRewards } from '../payment/use-claim-reward';
import { Button } from '@wwe-portal/ui/components/button';
import { verifyPosterString } from '@wwe-portal/ui/features/offer/offer-card/star-rarity';

export enum OfferRevampTypes {
  Feature = 'Feature',
  Daily = 'Daily',
  Rubies = 'Rubies',
  CashPacks = 'Cash Packs',
  Reward = 'Reward',
  Webcoin = 'Web Coin',
}

interface SelectedOfferProps {
  offerData: OfferData;
  pointRule: PointRule | undefined;
}

interface OfferPreviewContentProps {
  offerData: OfferData;
  pointRule: PointRule | undefined;
  onStartPayment: (payload: { offerData: OfferData }) => void;
  onClose?: () => void;
  // analytics data
  uiParameter: Partial<SysUIParameter>;
}

export const OfferPreviewContent = createPortableDialog(
  ({
    offerData,
    pointRule,
    onStartPayment,
    onClose,
    uiParameter: overriddenParameter,
  }: OfferPreviewContentProps) => {
    const t = useTranslations();
    const modal = usePortableDialog();
    const pendingPayment = usePendingPayment();
    const isClaiming = useIsClaimingRewards(offerData.OffersID);
    const buttonLoading =
      isClaiming || pendingPayment?.offerData.OffersID === offerData.OffersID;
    const { data: currentUser } = useGetUserInfo();
    const { data: currencies } = useGetCurrencies();
    const { offerMap } = useStoreData();
    const isAuthenticated = useIsAuthenticated();
    const dismissOto = useDismissOtoOffer();

    const handlePurchaseClick = () => {
      let index = 0;
      let position = 0;
      offerMap.forEach((value) => {
        if (value.OffersID === offerData.OffersID) {
          position = index;
          return false;
        }
        index++;
      });

      sendOfferButtonIsTappedEvent({
        overriddenParameter,
        offerData,
        position,
      });
      onStartPayment({
        offerData,
      });
    };

    const parseOfferItem = (offer: OfferItem) => {
      return {
        defaultImageUrl: offer.images?.url ?? '',
        dynamicImageUrl: offer.url ?? '',
        id: offer.itemID,
        itemId: offer.itemID,
        text: formatQuantityByDouble(offer.quantity),
        description: `${offer.quantity} ${offer.itemName}`,
        label: (offer as any).label,
        isBonusItem: verifyPosterString(offer.itemID),
      };
    };

    const parseOfferPreview = (offer: SelectedOfferProps) => {
      const { offerData, pointRule } = offer;
      const usdPrice = offerData.Price?.USD;
      const { isComingSoon, isNotReady } = getCardStatus(offerData);
      const statusLabel = cardStatusLabel({
        isComingSoon,
        isNotReady,
      });

      const firstRewardAmount =
        usdPrice && pointRule
          ? `${pointRule.rate * Math.round(usdPrice) * pointRule.value}`
          : '';
      const price = parseRegionalOfferPrice(offerData);
      const result: OfferReviewProps = {
        title: offerData?.StoreItemName ?? '',
        items: [
          ...(offerData?.offerItems?.map(parseOfferItem) ?? []),
          ...(offerData?.factionGiftItems?.map(parseOfferItem) ?? []),
        ],
        rewardItems: getRewardItems(offerData, currencies?.data.data),
        rewards: firstRewardAmount
          ? [
              `${firstRewardAmount} Points towards ALL active Store Bonus Reward Events!`,
            ]
          : undefined,
        endDate: getEndDate(offerData.EndDate),
        id: offerData?.OffersID ?? '',
        vipPoints:
          offerData.VipPoint ??
          0 + (currentUser?.pointBoost ?? 0) * (offerData.VipPoint ?? 0),
        disabled: false,
        storeItemDescription: isDripOffer(offerData)
          ? t('drip_offer_description', {
              days: offerData.dc?.b0 ?? DEFAULT_DRIP_DAYS,
            })
          : offerData.StoreItemDescription,
        actionBtn: statusLabel ? (
          <Button className="w-full" disabled size="large">
            {statusLabel}
          </Button>
        ) : (
          <PurchaseButton
            price={price.price}
            onClick={handlePurchaseClick}
            loading={buttonLoading}
            className="w-full"
          />
        ),
      };
      return result;
    };
    const data = parseOfferPreview({ offerData, pointRule });

    const closeDialog = () => {
      modal.remove();
      onClose?.();
    };

    const handleClose = () => {
      // A One Time Offer is gone for good once the user closes it, so ask
      // for confirmation and dismiss it on the game server before closing.
      if (isAuthenticated && isOneTimeOffer(offerData)) {
        showDialog(OtoCloseConfirmDialog, {
          onConfirm: () => {
            dismissOto.mutate(offerData.OffersID);
            closeDialog();
          },
        });
        return;
      }
      closeDialog();
    };
    return (
      <OfferReviewDialog
        onClose={handleClose}
        open={modal.visible}
        data={{ ...data }}
      />
    );
  }
);

registerDialog(DIALOG_ID_SPECIAL_OFFER_PREVIEW, OfferPreviewContent);

export const useOfferPreviewDialog = ({
  onStartPayment,
  onClose,
}: {
  onStartPayment: (payload: { offerData: OfferData }) => void;
  onClose?: () => void;
}) => {
  const { data: currentUser } = useGetUserInfo();
  const { offerMap } = useStoreData();
  const markViewedOffers = usePipStore((state) => state.viewOffers);
  const closePreviewDialog = (offer: OfferData) => {
    removeDialog(DIALOG_ID_SPECIAL_OFFER_PREVIEW);

    let index = 0;
    let position = 0;
    offerMap.forEach((value) => {
      if (value.OffersID === offer.OffersID) {
        position = index;
        return false;
      }
      index++;
    });

    specialOfferDialogClosed({
      offerId: offer.OffersID,
      offerItems: offer.offerItems ?? [],
      position,
    });
    onClose?.();
  };
  const showPreviewDialog = (offer: SelectedOfferProps) => {
    showDialogById(DIALOG_ID_SPECIAL_OFFER_PREVIEW, {
      offerData: offer.offerData,
      pointRule: offer.pointRule,
      id: DIALOG_ID_SPECIAL_OFFER_PREVIEW,
      onStartPayment,
      onClose: () => {
        closePreviewDialog(offer.offerData);
      },
    });

    let index = 0;
    let position = 0;
    offerMap.forEach((value) => {
      if (value.OffersID === offer.offerData.OffersID) {
        position = index;
        return false;
      }
      index++;
    });

    specialOfferDialogShowed({
      offerId: offer.offerData.OffersID,
      offerItems: offer.offerData.offerItems ?? [],
      position,
    });

    if (currentUser?.userId)
      markViewedOffers(currentUser.userId, [
        {
          id: offer.offerData.OffersID,
          endDate: offer.offerData.EndDate
            ? new Date(offer.offerData.EndDate)
            : undefined,
        },
      ]);
  };

  return {
    closePreview: closePreviewDialog,
    showPreview: showPreviewDialog,
  };
};
